import { get } from 'svelte/store';

import { devices_config_wave } from 'src/devices_config_wave';
import { get_z_wave_component_from_category, is_gen_z, real_id } from 'src/helpers/interface_helpers';
import {
	getZWaveDeviceConfigName,
	getZWaveGeneratedDeviceName,
	getZWaveProductType,
} from 'src/helpers/z_wave_gateway_helpers';
import { postReq } from 'src/services/http';
import { devices, devices_settings, devices_status } from 'src/stores';
import type { Device } from 'src/types/Device';

export type ZWaveChannelBackfill = {
	create: Device[];
	rename: Record<string, Device>;
};

/** A trailing `(203)` proves a gateway node id (`_gwc`) resolved for this channel. */
const GENERATED_NAME_WITH_ID = /\(\d+\)$/;

/** The `2) ` prefix carried by secondary channel cards. */
const CHANNEL_NAME_PREFIX = /^\d+\)\s*/;

const countKeysWithPrefix = (keys: unknown, prefix: string): number => {
	if (!Array.isArray(keys)) return 0;
	return keys.filter((key) => typeof key === 'string' && key.startsWith(prefix)).length;
};

/**
 * Channel count for a Wave product.
 * Prefer live status (`device.components` / `xcomps`) over config — dual-output
 * switches report 2 channels while `devices_config_wave` still says 1.
 */
export const getZWaveExpectedChannelCount = (
	device: Device,
	status?: Record<string, any> | null,
): number => {
	const productType = getZWaveProductType(device);
	const waveConfig = productType ? devices_config_wave[productType] : undefined;
	const mode = Object.keys(waveConfig?.modes || {})[0];
	const fromConfig = Number(mode ? waveConfig?.modes[mode]?.channels : undefined);
	const fromDevice = Number(device?.channels_count);
	const localPrefix = get_z_wave_component_from_category(device);
	const gatewayPrefix = localPrefix; // switch: / cover: / light: / input:
	const fromComponents = countKeysWithPrefix(status?.device?.components, gatewayPrefix);
	const fromXcomps = countKeysWithPrefix(status?.xcomps, localPrefix);

	return Math.max(
		Number.isFinite(fromConfig) && fromConfig > 0 ? fromConfig : 0,
		Number.isFinite(fromDevice) && fromDevice > 0 ? fromDevice : 0,
		fromComponents,
		fromXcomps,
		1,
	);
};

/** `Z-Wave Input (202)` for the first channel, `2) Z-Wave Input (202)` for the rest. */
export const buildZWaveChannelName = (generatedName: string, channelIndex: number): string => {
	return channelIndex === 0 ? generatedName : `${channelIndex + 1}) ${generatedName}`;
};

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * True when a cloud name carries no user intent and may be replaced: empty, `unnamed`,
 * built from the raw device id, the bare product name, or an earlier generated label.
 */
export const isAutoGeneratedZWaveName = (
	name: string | undefined,
	device: Device,
	friendlyName: string,
): boolean => {
	const trimmed = (name || '').trim().replace(CHANNEL_NAME_PREFIX, '');
	if (!trimmed || trimmed.toLowerCase() === 'unnamed') return true;

	const baseId = real_id(device?.id);
	if (baseId && trimmed.toLowerCase().includes(baseId.toLowerCase())) return true;

	if (!friendlyName) return false;
	if (trimmed === friendlyName) return true;

	return new RegExp(`^${escapeForRegExp(friendlyName)} \\(\\d+\\)$`).test(trimmed);
};

const buildZWaveChannelCard = (
	baseDevice: Device,
	channelIndex: number,
	channelsCount: number,
): Device => {
	const baseId = real_id(baseDevice.id);
	return {
		...baseDevice,
		id: channelIndex === 0 ? baseId : `${baseId}_${channelIndex}`,
		channel: channelIndex,
		channels_count: channelsCount,
	};
};

/**
 * Works out which Wave channel cards are missing and which carry a placeholder name.
 * Pure over the passed maps, so a second pass over the same data returns nothing to do.
 * Status and settings are read at the base id because all channel cards of one node
 * share a single status blob.
 */
export const collectZWaveChannelBackfill = (
	devicesMap: Record<string, Device>,
	statuses: Record<string, any>,
	settings: Record<string, any>,
	baseDeviceId?: string,
): ZWaveChannelBackfill => {
	const create: Device[] = [];
	const rename: Record<string, Device> = {};

	Object.values(devicesMap || {}).forEach((baseDevice) => {
		if (!is_gen_z(baseDevice) || baseDevice.shared) return;

		const baseId = real_id(baseDevice.id);
		if (baseDevice.id !== baseId) return;
		if (baseDeviceId && baseId !== baseDeviceId) return;

		const productType = getZWaveProductType(baseDevice);
		const friendlyName = productType ? devices_config_wave[productType]?.friendly_name : '';
		if (!friendlyName) return;

		const baseStatus = statuses?.[baseId];
		const baseSettings = settings?.[baseId];
		const channelsCount = getZWaveExpectedChannelCount(baseDevice, baseStatus);

		for (let channelIndex = 0; channelIndex < channelsCount; channelIndex++) {
			const channelCard = buildZWaveChannelCard(baseDevice, channelIndex, channelsCount);
			const generatedName = getZWaveGeneratedDeviceName(channelCard, baseStatus, baseSettings);
			if (!GENERATED_NAME_WITH_ID.test(generatedName)) continue;

			// Prefix `2) ` only when channels share the same node id (no per-output vcomp).
			// Multi-output nodes already get unique numbers (200 vs 201) from components.
			const channel0Name = getZWaveGeneratedDeviceName(
				buildZWaveChannelCard(baseDevice, 0, channelsCount),
				baseStatus,
				baseSettings,
			);
			const channelName =
				channelIndex > 0 && generatedName === channel0Name
					? buildZWaveChannelName(generatedName, channelIndex)
					: generatedName;
			const existing = devicesMap[channelCard.id];

			if (!existing) {
				create.push({
					...channelCard,
					name: channelName,
					position: (Number(baseDevice.position) || 0) + channelIndex,
				});
				continue;
			}

			if (getZWaveDeviceConfigName(existing, baseStatus, baseSettings)) continue;
			if (existing.name === channelName && existing.channels_count === channelsCount) continue;
			if (!isAutoGeneratedZWaveName(existing.name, baseDevice, friendlyName)) continue;

			rename[existing.id] = {
				...existing,
				name: channelName,
				channel: channelIndex,
				channels_count: channelsCount,
			};
		}
	});

	return { create, rename };
};

/**
 * Creates the Wave channel cards the backend never made and names the cards that
 * still carry a placeholder. Safe to call repeatedly; writes nothing when up to date.
 * Pass a base device id to limit the pass to a single freshly included device.
 */
export const backfillZWaveDeviceChannels = async (baseDeviceId?: string): Promise<void> => {
	try {
		const { create, rename } = collectZWaveChannelBackfill(
			get(devices),
			get(devices_status),
			get(devices_settings),
			baseDeviceId,
		);

		const renameIds = Object.keys(rename);
		if (create.length === 0 && renameIds.length === 0) return;

		await Promise.allSettled(
			create.map(async (channelCard) => {
				const response = await postReq('interface/device/save', {
					id: channelCard.id,
					data: JSON.stringify(channelCard),
				});
				if (response?.isok) {
					devices.update(channelCard);
				} else {
					console.error('Failed to create Z-Wave channel card', channelCard.id, response);
				}
			}),
		);

		if (renameIds.length === 0) return;

		const response = await postReq('interface/device/bulk_update', {
			devices_data: JSON.stringify(rename),
		});
		if (response?.isok) {
			devices.massUpdate(rename);
		} else {
			console.error('Failed to name Z-Wave channel cards', renameIds, response);
		}
	} catch (error) {
		console.error('Z-Wave channel backfill failed', error);
	}
};
